import { Injectable } from '@angular/core';
import { IVendorInfo } from '../state/vendor-state.service';
import * as Parse from 'parse';
import { AuthService } from './auth.service';
import { OrganizationService } from './organization.service';

export type VendorDocumentCategory = 'proofOfLicense' |
                                     'proofOfInsurance' |
                                     'corporationDocuments' |
                                     'other';

export interface IVendor {
    companyName: string;
    contactName: string;
    phone: string;
    email: string;
    licenseNumber: string;
    type: string;
    type2: string;
    password?: string;
    password2?: string;
}

@Injectable()
export class VendorService {

    constructor(private auth: AuthService, private orgService: OrganizationService) {
    }

    assembleMainQuery() {
        return new Parse.Query('Vendor').equalTo('organization', this.orgService.organization);
    }

    fetchVendors(query: Parse.Query, pageIndex: number, pageSize: number, withCount: boolean = true) {
        query.limit(pageSize);
        query.skip(pageIndex * pageSize);
        query.ascending('companyName');
        query.include('user');

        if (withCount) {
            query.withCount();
        }

        return query.find();
    }

    getCompanyFilter(searchTerm: string) {
        if (searchTerm === '') {
            return false;
        }

        const keyword: string = searchTerm.toLowerCase().trim();

        let companyNameQuery: Parse.Query = new Parse.Query('Vendor');
        companyNameQuery.contains('companyName', keyword);

        let contactNameQuery: Parse.Query = new Parse.Query('Vendor');
        contactNameQuery.contains('contactName', keyword);

        return Parse.Query.or(companyNameQuery, contactNameQuery);
    }

    getTypeFilter(filterBy: string) {
        if (filterBy === 'all') {
            return false;
        }

        return new Parse.Query('Vendor').equalTo('type', filterBy);
    }

    getCategoryFilter(searchTerm: string) {
        if (searchTerm === '') {
            return false;
        }

        const companyNameQuery: Parse.Query = new Parse.Query('Vendor').contains('companyName', searchTerm.trim().toLowerCase());
        const typeQuery: Parse.Query = new Parse.Query('Vendor').contains('type', searchTerm.trim().toLowerCase());
        const type2Query: Parse.Query = new Parse.Query('Vendor').contains('type2', searchTerm.trim().toLowerCase());

        return Parse.Query.or(companyNameQuery, typeQuery, type2Query);
    }

    fetchVendorById(id: string) {
        return new Parse.Query('Vendor').equalTo('objectId', id).equalTo('organization', this.orgService.organization).include('user').first();
    }

    fetchVendorByUserId(ids: string[], pageSize: number, currentPage: number) {
        let user = new Parse.User();
        user.id = ids[0];
        let query = new Parse.Query('Vendor').equalTo('user', user).include('user');

        for (let i = 1, l = ids.length; i < l; i++) {
            user = new Parse.User();
            user.id = ids[i];

            query = Parse.Query.or(query, new Parse.Query('Vendor').equalTo('user', user)).include('user');
        }

        query.limit(pageSize);
        query.skip(pageSize * currentPage);
        query.ascending('companyName');

        return query.find();
    }

    confirmVendor(vendor: Parse.Object, newVendor: IVendor) {
        return Parse.Cloud.run('confirmVendor', { vendorId: vendor.id, newVendor: newVendor });
    }

    async createVendor(vendor: Parse.Object, info: IVendorInfo) {
        const user: Parse.User = new Parse.User();
        const phone: string = info.phone.trim().replace(/\D+/g, '');

        user.set('username', info.email.trim().toLowerCase());
        user.set('email', info.email.trim().toLowerCase());
        user.set('password', Math.random().toString(16) + Math.random());
        user.set('type', 'Vendor');
        user.set('organization', this.orgService.organization);

        if (phone !== '') {
            user.set('phone', phone);
        }

        const ACL: Parse.ACL = new Parse.ACL();

        ACL.setRoleReadAccess(this.orgService.organization.id + '_Admin', true);
        ACL.setRoleReadAccess(this.orgService.organization.id + '_PM', true);

        user.setACL(ACL);

        const newUser: Parse.User = await user.save();

        if (newUser) {
            vendor.set('user', newUser);
            vendor.set('companyName', info.companyName.trim().toLowerCase());
            vendor.set('contactName', info.contactName.trim().toLowerCase());
            vendor.set('licenseNumber', info.licenseNumber.trim());
            vendor.set('type', info.type.trim());
            vendor.set('type2', info.type2?.trim());
            vendor.set('organization', this.orgService.organization);

            return vendor.save();
        }

        return new Error('Unable to save user.');
    }

    updateVendor(vendor: Parse.Object, info: IVendorInfo) {
        const user: Parse.User = vendor.get('user');
        const phone: string = info.phone.trim().replace(/\D+/g, '');

        if (phone !== '') {
            user.set('phone', phone);
        }

        if (user.get('username').trim().toLowerCase() !== info.email.trim().toLowerCase()) {
            user.setEmail(info.email.trim().toLowerCase());
            user.setUsername(info.email.trim().toLowerCase());
        }

        vendor.set('companyName', info.companyName.trim().toLowerCase());
        vendor.set('contactName', info.contactName.trim().toLowerCase());
        vendor.set('licenseNumber', info.licenseNumber.trim());
        vendor.set('type', info.type.trim());
        vendor.set('type2', info.type2?.trim());

        return Promise.all([user.save(), vendor.save()]);
    }

    assignVendorToProperty(property: Parse.Object, vendors: Parse.Object[]) {
        property.addAllUnique('vendors', vendors);

        return property.save(null, { cascadeSave: false });
    }

    unassignVendor(property: Parse.Object, vendor: Parse.Object) {
        property.remove('vendors', vendor);

        return property.save(null, { cascadeSave: false });
    }
}
